import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  ChevronLeft,
  Download,
  Landmark as LandmarkIcon,
  MapPinOff,
  MountainSnow,
  Move,
  Pencil,
  Plus,
  RotateCcw,
  Share2,
  Tent,
  Trash2,
  Unlink,
  X,
} from "lucide-react";
import { errorMessage, TRIP_JOIN_MIN_DRAW_M, TRIP_MAX_ACTIVITIES, type TripDetailResponse } from "@atlas/shared";
import { deleteTrip, downloadTripGpx, fetchTrip, resetTripCamp, setTripCamp, updateTrip } from "../api/trips";
import { fetchActivities } from "../api/activities";
import { AtlasPanel } from "../components/AtlasPanel";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { ProgressBar } from "../components/ProgressBar";
import { ShareTripDialog } from "../components/ShareTripDialog";
import { TripRoutePanel } from "../components/TripRoutePanel";
import { Skel } from "../components/Skeleton";
import { StatBlock } from "../components/StatBlock";
import { PoweredByStrava, stravaActivityUrl } from "../components/StravaBrand";
import { TripMapLegend, TripRouteMap, tripDayColor } from "../components/map/TripRouteMap";
import { usePageMeta } from "../hooks/usePageMeta";
import { useTripGrouping } from "../hooks/useTripGrouping";
import {
  formatGapDistance,
  formatTripDates,
  formatTripDayLabel,
  formatTripDistance,
  formatTripDuration,
  formatTripElevation,
  plural,
} from "../lib/trip-format";

// Trip page (spec 35 / ATL-157): several activities shown as one walk. Every
// figure comes from the API, summed / unioned from the live members. The gaps
// between recordings are drawn dashed and reported separately; they never
// count towards the distance.

const iconButton: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "var(--text-muted)",
  padding: 4,
  display: "inline-flex",
  borderRadius: "var(--radius-sm)",
};

function TripName({ tripId, name }: { tripId: string; name: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(name);
  React.useEffect(() => setDraft(name), [name]);
  const mutation = useMutation({
    mutationFn: (next: string) => updateTrip(tripId, { name: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["trips"] });
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      setEditing(false);
    },
  });

  const save = () => {
    const next = draft.trim();
    if (!next || next === name) {
      setDraft(name);
      setEditing(false);
      return;
    }
    mutation.mutate(next);
  };

  if (editing) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
        <input
          autoFocus
          value={draft}
          maxLength={120}
          aria-label="Trip name"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") {
              setDraft(name);
              setEditing(false);
            }
          }}
          onBlur={save}
          style={{
            font: "var(--type-display)",
            fontSize: 34,
            letterSpacing: "-.02em",
            color: "var(--text-primary)",
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-md)",
            padding: "4px 10px",
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        />
        {mutation.isError && (
          <span style={{ fontSize: 12, color: "var(--status-danger)" }}>
            {errorMessage(mutation.error, "Couldn't rename the trip.")}
          </span>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
      <h1
        style={{
          font: "var(--type-display)",
          fontSize: 38,
          lineHeight: 1.1,
          letterSpacing: "-.02em",
          color: "var(--text-primary)",
          margin: 0,
          overflowWrap: "anywhere",
          minWidth: 0,
        }}
      >
        {name}
      </h1>
      <button type="button" aria-label="Rename trip" title="Rename" style={{ ...iconButton, marginTop: 8 }} onClick={() => setEditing(true)}>
        <Pencil size={16} />
      </button>
    </div>
  );
}

function AddActivitiesDialog({
  tripId,
  memberIds,
  onClose,
}: {
  tripId: string;
  memberIds: Set<string>;
  onClose: () => void;
}) {
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set());
  const query = useQuery({ queryKey: ["activities", { recentForTrip: true }], queryFn: () => fetchActivities(40, 0) });
  const grouping = useTripGrouping({ onDone: () => onClose() });
  const candidates = (query.data?.activities ?? []).filter((a) => !memberIds.has(a.id));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add activities to this trip"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 24,
          maxWidth: 520,
          width: "100%",
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          boxShadow: "var(--shadow-xl)",
          minWidth: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>Add activities</h2>
          <button type="button" aria-label="Close" style={iconButton} onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-secondary)" }}>Your 40 most recent activities.</p>
        <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 4, minHeight: 0 }}>
          {query.isLoading ? (
            [0, 1, 2, 3].map((i) => <Skel key={i} height="44px" />)
          ) : candidates.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>Nothing else to add.</p>
          ) : (
            candidates.map((a) => {
              const on = selected.has(a.id);
              return (
                <label
                  key={a.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    borderRadius: "var(--radius-md)",
                    border: `1px solid ${on ? "var(--border-gold)" : "var(--border-subtle)"}`,
                    background: on ? "var(--accent-soft)" : "var(--surface-sunken)",
                    cursor: "pointer",
                    minWidth: 0,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(a.id)) next.delete(a.id);
                        else next.add(a.id);
                        return next;
                      })
                    }
                    style={{ accentColor: "var(--gold-500)" }}
                  />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.name}
                  </span>
                  <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)", flexShrink: 0 }}>
                    {new Date(a.startedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    {a.distanceM != null ? ` · ${formatTripDistance(a.distanceM)}` : ""}
                  </span>
                </label>
              );
            })
          )}
        </div>
        {grouping.error && <p style={{ margin: 0, fontSize: 12.5, color: "var(--status-danger)" }}>{grouping.error}</p>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus size={13} />}
            disabled={selected.size === 0 || grouping.pending}
            onClick={() => grouping.addToTrip(tripId, [...selected])}
          >
            {grouping.pending ? "Adding…" : selected.size > 0 ? `Add ${plural(selected.size, "activity", "activities")}` : "Add"}
          </Button>
        </div>
        {grouping.confirmElement}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="eyebrow" style={{ fontSize: 10.5, marginBottom: 8 }}>
      {children}
    </div>
  );
}

/** Round 2: the bar over the map while a camp is being moved. */
function CampPickBar({
  night,
  picked,
  name,
  onName,
  saving,
  error,
  onSave,
  onCancel,
}: {
  night: number;
  picked: boolean;
  name: string;
  onName: (name: string) => void;
  saving: boolean;
  error: string | null;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-label={`Move the camp for night ${night}`}
      className="atlas-glass"
      style={{
        position: "absolute",
        top: 12,
        left: 12,
        right: 60,
        zIndex: 6,
        padding: "10px 12px",
        borderRadius: "var(--radius-md)",
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        fontSize: 13,
        color: "var(--text-primary)",
      }}
    >
      <Tent size={15} style={{ flexShrink: 0, color: "var(--text-secondary)" }} />
      {!picked ? (
        <span style={{ flex: "1 1 200px", minWidth: 0 }}>Click the map where you camped on night {night}.</span>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
          style={{ display: "flex", alignItems: "center", gap: 8, flex: "1 1 260px", minWidth: 0, flexWrap: "wrap" }}
        >
          <input
            value={name}
            onChange={(e) => onName(e.target.value)}
            placeholder="Name (optional), e.g. Chapel House Farm"
            maxLength={120}
            aria-label="Camp name"
            autoFocus
            style={{
              flex: "1 1 180px",
              minWidth: 0,
              height: 32,
              padding: "0 10px",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border-default)",
              background: "var(--surface-sunken)",
              color: "var(--text-primary)",
              fontFamily: "var(--font-sans)",
              fontSize: 13,
            }}
          />
          <Button type="submit" variant="secondary" size="sm" disabled={saving}>
            {saving ? "Saving…" : "Save camp"}
          </Button>
        </form>
      )}
      <Button variant="ghost" size="sm" onClick={onCancel}>
        Cancel
      </Button>
      {error && <span style={{ flexBasis: "100%", fontSize: 12, color: "var(--status-danger)" }}>{error}</span>}
    </div>
  );
}

function TripContent({ data }: { data: TripDetailResponse }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trip, members, joins, peaks, landmarks, trails, regions } = data;
  // Spec 38 fields; absent on an API that predates it.
  const fills = data.fills ?? [];
  const routePeaks = data.routePeaks ?? [];
  const filledM = trip.filledM ?? 0;
  const [sharing, setSharing] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [editingMembers, setEditingMembers] = React.useState(false);
  const [confirmUngroup, setConfirmUngroup] = React.useState(false);
  const [removing, setRemoving] = React.useState<string | null>(null);
  const [gpxState, setGpxState] = React.useState<"idle" | "busy" | "error">("idle");
  // Round 2: moving a camp. `fromActivityId` = the member before that night.
  const [campPick, setCampPick] = React.useState<{ fromActivityId: string; night: number; name: string } | null>(null);
  const [pendingCamp, setPendingCamp] = React.useState<{ lat: number; lng: number } | null>(null);
  const mapBoxRef = React.useRef<HTMLDivElement>(null);

  usePageMeta({ title: `${trip.name} | Atlas`, description: `A trip on Atlas: ${trip.name}.`, canonicalPath: `/trips/${trip.id}` });

  const ungroup = useMutation({
    mutationFn: () => deleteTrip(trip.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["trips"] });
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      void navigate({ to: "/activities" });
    },
  });
  const removeMember = useMutation({
    mutationFn: (activityId: string) => updateTrip(trip.id, { removeActivityIds: [activityId] }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["trips"] });
      // Activity rows and screens show "Part of {trip}".
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
    onSettled: () => setRemoving(null),
  });

  const cancelCampPick = React.useCallback(() => {
    setCampPick(null);
    setPendingCamp(null);
  }, []);
  const refreshTrip = () => {
    void queryClient.invalidateQueries({ queryKey: ["trips", trip.id] });
    void queryClient.invalidateQueries({ queryKey: ["trips"] });
    // A moved camp re-cuts the fills, which draw on the exploration map.
    void queryClient.invalidateQueries({ queryKey: ["map/heatmap"] });
  };
  const saveCamp = useMutation({
    mutationFn: (v: { fromActivityId: string; lat: number; lng: number; name: string }) =>
      setTripCamp(trip.id, v.fromActivityId, { latitude: v.lat, longitude: v.lng, name: v.name.trim() || null }),
    onSuccess: () => {
      cancelCampPick();
      refreshTrip();
    },
  });
  const resetCamp = useMutation({
    mutationFn: (fromActivityId: string) => resetTripCamp(trip.id, fromActivityId),
    onSuccess: refreshTrip,
  });
  React.useEffect(() => {
    if (!campPick) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelCampPick();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [campPick, cancelCampPick]);
  const startCampPick = (fromActivityId: string, night: number, name: string | null) => {
    saveCamp.reset();
    setPendingCamp(null);
    setCampPick({ fromActivityId, night, name: name ?? "" });
    // Instant: the map is far above the Days list on a phone.
    mapBoxRef.current?.scrollIntoView({ block: "start" });
  };

  const tracks = React.useMemo(() => members.map((m) => ({ dayIndex: m.dayIndex, geojson: m.geojson })), [members]);
  const mapJoins = React.useMemo(
    () =>
      joins.map((j) => ({
        distanceM: j.distanceM,
        overnight: j.overnight,
        filled: j.filled,
        geometry: j.geometry,
        camp: j.camp ?? null,
      })),
    [joins],
  );
  const mapFills = React.useMemo(() => fills.map((f) => ({ dayIndex: f.dayIndex, geometry: f.geometry })), [fills]);
  const drawnJoins = joins.filter((j) => !j.filled && j.distanceM >= TRIP_JOIN_MIN_DRAW_M);
  const unrecordedJoins = drawnJoins.filter((j) => !j.overnight);
  const memberIds = React.useMemo(() => new Set(members.map((m) => m.activityId)), [members]);
  const hasReached = peaks.length + landmarks.length > 0;
  const newCount = peaks.filter((p) => p.isNew).length + landmarks.filter((l) => l.isNew).length;
  const peakIds = new Set(peaks.map((p) => p.id));
  // Trig pillars on a reached summit fold into the peak, as on the map.
  const shownLandmarks = landmarks.filter((l) => !(l.category === "trig_point" && l.peakId && peakIds.has(l.peakId)));

  async function handleGpx() {
    setGpxState("busy");
    try {
      await downloadTripGpx(trip.id, trip.name);
      setGpxState("idle");
    } catch {
      setGpxState("error");
      setTimeout(() => setGpxState("idle"), 2500);
    }
  }

  return (
    <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto", minWidth: 0 }}>
      <Link
        to="/activities"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          alignSelf: "flex-start",
          color: "var(--text-secondary)",
          fontSize: 14,
          fontWeight: 600,
          textDecoration: "none",
        }}
      >
        <ChevronLeft size={18} /> Activities
      </Link>

      {/* Hero */}
      <div
        className="atlas-topo"
        style={{
          position: "relative",
          borderRadius: "var(--radius-xl)",
          border: "1px solid var(--border-default)",
          padding: 28,
          overflow: "hidden",
          boxShadow: "var(--shadow-md)",
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <div className="r-wrap" style={{ position: "relative", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 360px", minWidth: 0 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              Trip · {formatTripDates(trip.startedAt, trip.endedAt)}
            </div>
            <TripName tripId={trip.id} name={trip.name} />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <Badge variant="neutral">{plural(trip.activityCount, "activity", "activities")}</Badge>
              {newCount > 0 && (
                <Badge variant="gold" dot>
                  {newCount} new
                </Badge>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flexShrink: 0 }}>
            <Button variant="primary" leftIcon={<Share2 size={16} />} onClick={() => setSharing(true)} disabled={trip.activityCount === 0}>
              Share
            </Button>
            <Button variant="secondary" leftIcon={<Download size={16} />} onClick={() => void handleGpx()} disabled={gpxState === "busy" || trip.activityCount === 0}>
              {gpxState === "busy" ? "Preparing…" : gpxState === "error" ? "Couldn't download" : "GPX"}
            </Button>
          </div>
        </div>

        <div
          className="r-cols-2"
          style={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            gap: 16,
            marginTop: 24,
          }}
        >
          <StatBlock value={formatTripDistance(trip.distanceM + filledM)} label="Distance" size="sm" />
          <StatBlock value={formatTripElevation(trip.elevationGainM)} label="Ascent" size="sm" />
          <StatBlock value={formatTripDuration(trip.durationSeconds)} label="Time" size="sm" />
          <StatBlock value={String(trip.days)} label={trip.days === 1 ? "Day" : "Days"} size="sm" />
          <StatBlock value={String(trip.nights)} label={trip.nights === 1 ? "Night out" : "Nights out"} size="sm" />
        </div>

        {filledM > 0 && (
          <p style={{ position: "relative", margin: "16px 0 0", fontSize: 13, color: "var(--text-secondary)" }}>
            {formatTripDistance(filledM)} of it from your planned route, filling gaps between recordings. Shown
            dotted on the map.
          </p>
        )}
        {trip.unrecordedM > 0 && (
          <p style={{ position: "relative", margin: "16px 0 0", fontSize: 13, color: "var(--text-secondary)" }}>
            {formatGapDistance(trip.unrecordedM)} not recorded between activities
            {unrecordedJoins.length > 1 ? ` (${unrecordedJoins.length} gaps)` : ""}. Shown dashed on the map, never added
            to the distance.
          </p>
        )}
        {trip.parkedCount > 0 && (
          <p style={{ position: "relative", margin: "8px 0 0", fontSize: 13, color: "var(--text-muted)" }}>
            {trip.parkedCount === 1
              ? "1 activity in this trip is removed. Restore it to bring it back."
              : `${trip.parkedCount} activities in this trip are removed. Restore them to bring them back.`}{" "}
            <Link to="/activities" search={{ removed: 1 }} style={{ color: "var(--text-accent)", textDecoration: "none" }}>
              Removed activities
            </Link>
          </p>
        )}
      </div>

      {/* Map */}
      {members.length > 0 ? (
        <div
          ref={mapBoxRef}
          style={{
            position: "relative",
            // Clear the sticky top bars when "Move" scrolls the map into view.
            scrollMarginTop: 80,
            height: "clamp(380px, 55vh, 640px)",
            flexShrink: 0,
            borderRadius: "var(--radius-lg)",
            overflow: "hidden",
            border: "1px solid var(--border-subtle)",
            boxShadow: "var(--ring-top)",
          }}
        >
          <TripRouteMap
            key={trip.id}
            tracks={tracks}
            joins={mapJoins}
            fills={mapFills}
            peaks={peaks}
            landmarks={landmarks}
            onPinClick={(pin) => void navigate({ to: pin.kind === "peak" ? "/peaks/$slug" : "/landmarks/$slug", params: { slug: pin.slug } })}
            pickMode={campPick !== null}
            onMapPick={(p) => {
              saveCamp.reset();
              setPendingCamp(p);
            }}
            pendingPin={pendingCamp}
          />
          {campPick && (
            <CampPickBar
              night={campPick.night}
              picked={pendingCamp !== null}
              name={campPick.name}
              onName={(name) => setCampPick({ ...campPick, name })}
              saving={saveCamp.isPending}
              error={saveCamp.isError ? errorMessage(saveCamp.error, "Couldn't move the camp. Try again.") : null}
              onSave={() =>
                pendingCamp &&
                saveCamp.mutate({ fromActivityId: campPick.fromActivityId, lat: pendingCamp.lat, lng: pendingCamp.lng, name: campPick.name })
              }
              onCancel={cancelCampPick}
            />
          )}
          <TripMapLegend
            days={trip.days}
            hasJoins={drawnJoins.length > 0}
            hasCamps={joins.some((j) => j.overnight)}
            hasFills={mapFills.length > 0}
          />
        </div>
      ) : (
        <AtlasPanel>
          <EmptyState
            icon={<MapPinOff size={18} />}
            title="No activities to show"
            hint="Every activity in this trip is removed. Restore one to bring the trip back."
          />
        </AtlasPanel>
      )}

      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)", gap: 20, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
          {/* What it reached */}
          <AtlasPanel title="What it reached">
            {!hasReached ? (
              <p style={{ margin: 0, fontSize: 13.5, color: "var(--text-muted)" }}>No peaks or landmarks on this trip.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {peaks.length > 0 && (
                  <div>
                    <SectionLabel>{plural(peaks.length, "peak")}</SectionLabel>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {peaks.map((p) => (
                        <Link
                          key={p.id}
                          to="/peaks/$slug"
                          params={{ slug: p.slug }}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: "var(--radius-md)", textDecoration: "none", color: "var(--text-primary)", minWidth: 0 }}
                        >
                          <MountainSnow size={15} style={{ color: "var(--text-accent)", flexShrink: 0 }} />
                          <span style={{ flex: 1, minWidth: 0, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                          {p.elevationM != null && (
                            <span className="tnum" style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                              {Math.round(p.elevationM).toLocaleString("en-GB")} m
                            </span>
                          )}
                          {p.isNew && <Badge variant="gold">New</Badge>}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                {shownLandmarks.length > 0 && (
                  <div>
                    <SectionLabel>{plural(shownLandmarks.length, "landmark")}</SectionLabel>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {shownLandmarks.map((l) => (
                        <Link
                          key={l.id}
                          to="/landmarks/$slug"
                          params={{ slug: l.slug }}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: "var(--radius-md)", textDecoration: "none", color: "var(--text-primary)", minWidth: 0 }}
                        >
                          <LandmarkIcon size={15} style={{ color: "var(--status-info)", flexShrink: 0 }} />
                          <span style={{ flex: 1, minWidth: 0, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
                          {l.isNew && <Badge variant="gold">New</Badge>}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </AtlasPanel>

          {/* Trails */}
          {trails.length > 0 && (
            <AtlasPanel title="Trails">
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {trails.map((t) => (
                  <div key={t.id} style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, minWidth: 0 }}>
                      <Link to="/trails/$id" params={{ id: t.slug }} style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", textDecoration: "none", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.name}
                      </Link>
                      <span className="tnum" style={{ fontSize: 13, color: "var(--text-secondary)", flexShrink: 0 }}>
                        {t.kmWalked.toFixed(1)} km on this trip
                      </span>
                    </div>
                    <ProgressBar value={t.progressPct} max={100} size="sm" color={t.progressPct >= 100 ? "gold" : "sky"} label="Trail progress" />
                    <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                      {t.sectionsCompleted > 0
                        ? `${t.sectionsCompleted} of ${t.totalSections} sections completed on this trip`
                        : `No sections completed on this trip · ${t.totalSections} in all`}
                    </div>
                    {t.alternates.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {t.alternates.map((a) => (
                          <Badge key={a.id} variant={a.coveragePct >= 100 ? "gold" : "info"}>
                            {a.name} route · {a.coveragePct}%
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </AtlasPanel>
          )}

          {regions.length > 0 && (
            <AtlasPanel title="Regions">
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {regions.map((r) => (
                  <Link
                    key={r.id}
                    to="/regions/$slug"
                    params={{ slug: r.slug }}
                    style={{
                      padding: "4px 10px",
                      borderRadius: "var(--radius-pill)",
                      background: "var(--surface-sunken)",
                      border: "1px solid var(--border-subtle)",
                      fontSize: 13,
                      color: "var(--text-primary)",
                      textDecoration: "none",
                    }}
                  >
                    {r.name}
                  </Link>
                ))}
              </div>
            </AtlasPanel>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
        <TripRoutePanel trip={trip} memberIds={memberIds} routePeaks={routePeaks} />

        {/* Days */}
        <AtlasPanel
          title="Days"
          action={
            <span style={{ display: "inline-flex", gap: 4 }}>
              {editingMembers ? (
                <Button variant="ghost" size="sm" onClick={() => setEditingMembers(false)}>
                  Done
                </Button>
              ) : (
                <Button variant="ghost" size="sm" leftIcon={<Pencil size={13} />} onClick={() => setEditingMembers(true)}>
                  Edit
                </Button>
              )}
            </span>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {members.map((m, i) => {
              const join = i > 0 ? joins.find((j) => j.toActivityId === m.activityId) : undefined;
              // Round 2: an overnight gap's fill is split at the camp, so a join can have two.
              const filledHere =
                i > 0 ? fills.filter((f) => f.toActivityId === m.activityId).reduce((sum, f) => sum + f.distanceM, 0) : 0;
              const night = join?.overnight ? joins.filter((j) => j.overnight).indexOf(join) + 1 : 0;
              return (
                <React.Fragment key={m.activityId}>
                  {join && join.overnight && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "4px 8px",
                        fontSize: 12,
                        color: "var(--text-muted)",
                        flexWrap: "wrap",
                        minWidth: 0,
                      }}
                    >
                      <Tent size={13} style={{ flexShrink: 0 }} />
                      <span style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        Camp{join.camp?.name ? ` · ${join.camp.name}` : ""}
                        {join.camp?.source === "user" ? " (moved)" : ""}
                      </span>
                      <span style={{ display: "inline-flex", gap: 2, flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => startCampPick(join.fromActivityId, night, join.camp?.name ?? null)}
                          disabled={campPick !== null}
                          style={{ ...iconButton, gap: 4, fontSize: 12, color: "var(--text-accent)", fontFamily: "var(--font-sans)" }}
                        >
                          <Move size={12} /> Move
                        </button>
                        {join.camp?.source === "user" && (
                          <button
                            type="button"
                            onClick={() => resetCamp.mutate(join.fromActivityId)}
                            disabled={resetCamp.isPending}
                            style={{ ...iconButton, gap: 4, fontSize: 12, color: "var(--text-secondary)", fontFamily: "var(--font-sans)" }}
                          >
                            <RotateCcw size={12} /> Reset to automatic
                          </button>
                        )}
                      </span>
                    </div>
                  )}
                  {filledHere > 0 && (
                    <div style={{ padding: "2px 8px 2px 30px", fontSize: 12, color: "var(--text-muted)" }}>
                      {formatTripDistance(filledHere)} from your route
                    </div>
                  )}
                  {join && !join.overnight && !join.filled && join.distanceM >= TRIP_JOIN_MIN_DRAW_M && (
                    <div style={{ padding: "2px 8px 2px 30px", fontSize: 12, color: "var(--text-muted)" }}>
                      {formatGapDistance(join.distanceM)} not recorded
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 10px",
                      background: "var(--surface-sunken)",
                      border: "1px solid var(--border-subtle)",
                      borderRadius: "var(--radius-md)",
                      minWidth: 0,
                    }}
                  >
                    <span aria-hidden style={{ width: 4, alignSelf: "stretch", borderRadius: 2, background: tripDayColor(m.dayIndex), flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="eyebrow" style={{ fontSize: 10 }}>{formatTripDayLabel(m.dayIndex, m.startedAt)}</div>
                      <Link
                        to="/activities/$id/review"
                        params={{ id: m.activityId }}
                        style={{ display: "block", fontSize: 14, fontWeight: 600, color: "var(--text-primary)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {m.name}
                      </Link>
                      <div className="tnum" style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
                        {m.distanceM != null ? formatTripDistance(m.distanceM) : "No distance"}
                        {m.durationSeconds ? ` · ${formatTripDuration(m.durationSeconds)}` : ""}
                        {m.sourceType === "strava" && m.sourceId && (
                          <>
                            {" · "}
                            <a href={stravaActivityUrl(m.sourceId)} target="_blank" rel="noreferrer" style={{ color: "var(--text-accent)", textDecoration: "none" }}>
                              View on Strava
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                    {editingMembers && (
                      <button
                        type="button"
                        aria-label={`Take ${m.name} out of this trip`}
                        title="Take out of this trip"
                        disabled={removeMember.isPending}
                        onClick={() => {
                          setRemoving(m.activityId);
                          removeMember.mutate(m.activityId);
                        }}
                        style={{ ...iconButton, color: "var(--text-secondary)", opacity: removing === m.activityId ? 0.5 : 1 }}
                      >
                        <Unlink size={15} />
                      </button>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
            {resetCamp.isError && (
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--status-danger)" }}>
                {errorMessage(resetCamp.error, "Couldn't reset the camp. Try again.")}
              </p>
            )}
            {removeMember.isError && (
              <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--status-danger)" }}>
                {errorMessage(removeMember.error, "Couldn't take that out. Try again.")}
              </p>
            )}
            {editingMembers && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
                  Taking an activity out of a trip leaves the activity itself untouched.
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Plus size={13} />}
                    disabled={trip.activityCount + trip.parkedCount >= TRIP_MAX_ACTIVITIES}
                    onClick={() => setAdding(true)}
                  >
                    Add activities
                  </Button>
                  <Button variant="danger" size="sm" leftIcon={<Trash2 size={13} />} onClick={() => setConfirmUngroup(true)}>
                    Ungroup trip
                  </Button>
                </div>
              </div>
            )}
            {trip.hasStravaMember && <PoweredByStrava height={30} style={{ marginTop: 12, opacity: 0.85 }} />}
          </div>
        </AtlasPanel>
        </div>
      </div>

      {sharing && <ShareTripDialog trip={trip} onClose={() => setSharing(false)} />}
      {adding && <AddActivitiesDialog tripId={trip.id} memberIds={memberIds} onClose={() => setAdding(false)} />}
      {confirmUngroup && (
        <ConfirmDialog
          title="Ungroup this trip?"
          body="The trip goes, and any public link to it stops working. Your activities stay exactly as they are."
          confirmLabel="Ungroup"
          pendingLabel="Ungrouping…"
          danger
          pending={ungroup.isPending}
          error={ungroup.isError ? errorMessage(ungroup.error, "Couldn't ungroup. Try again.") : null}
          onConfirm={() => ungroup.mutate()}
          onClose={() => setConfirmUngroup(false)}
        />
      )}
    </div>
  );
}

export default function TripDetailPage() {
  const { tripId } = useParams({ strict: false }) as { tripId: string };
  const query = useQuery({ queryKey: ["trips", tripId], queryFn: () => fetchTrip(tripId) });
  const navigate = useNavigate();

  if (query.isLoading) {
    return (
      <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
        <Skel height="200px" />
        <Skel height="420px" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="r-page" style={{ padding: 28, flex: 1 }}>
        <EmptyState
          icon={<MapPinOff size={18} />}
          title="Couldn't load this trip"
          hint={errorMessage(query.error, "It may have been ungrouped.")}
          action={{ label: "Back to activities", onClick: () => void navigate({ to: "/activities" }) }}
        />
      </div>
    );
  }
  return <TripContent data={query.data} />;
}
